import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import {
  AlertaAReceber,
  BarraFiltroRapido,
  Button,
  ButtonIcon,
  Container,
  DivAcoes,
  FlagSemArquivo,
  FlagUrgente,
  Header,
  PagamentoUrgenteContainer,
  CustosCelula,
  TagCustoExtra,
  TextoHeader,
} from "./style";
import { formatarDinheiro } from "_utils/Money";
import { IListarLoteGerenciarPagamento } from "../../../../../interface/IListarLoteGerenciarPagamento";
import {
  FaCheck,
  FaColumns,
  FaDownload,
  FaEye,
  FaExclamationTriangle,
  FaFlag,
  FaImage,
  FaPlus,
  FaTimes,
} from "react-icons/fa";
import { SwitchCustom } from "components/Switch";
import { Popover, Tooltip } from "react-bootstrap";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { utils, writeFileXLSX } from "xlsx";
import { useState } from "react";
import { ModalConfigColunas } from "../ModalConfigColunas";
import { defaultConfigColunas } from "../../constants/defaultConfigColunas";

const STORAGE_KEY = "config_listar_gerenciar_pagamentos";

interface IListarProps {
  dados: IListarLoteGerenciarPagamento[];
  handleLimitChange: (e: number) => void;
  handlePageChange: (e: number) => void;
  totalRows: number;
  setRowsSelected: (rows: IListarLoteGerenciarPagamento[]) => void;
  selectedRows: IListarLoteGerenciarPagamento[];

  linhasPorPagina: number;
  onClickAprovar: () => void;
  onClickReprovar: () => void;
  onClickVerDetalhes: (idLote: number) => void;
  clearRows: boolean;
  pagamentoUrgenteAtivo: boolean;
  onTogglePagamentoUrgente: (urgente: boolean) => void;
  onBaixarRecibo: (idLote: number, numeroNfRecibo: string) => void;
  onCriarLote: () => void;
  onOrdenar: (campo: string, direcao: string) => void;
  podeCriar: boolean;
  podeAprovar: boolean;
  podeReprovar: boolean;
}

function CustosColunaCell({ custos }: { custos: string[] }) {
  const MAX_VISIVEIS = 1;
  const visiveis = custos.slice(0, MAX_VISIVEIS);
  const extras = custos.length - MAX_VISIVEIS;

  const popover = (
    <Popover id="popover-custos" style={{ minWidth: 220 }}>
      <Popover.Body style={{ padding: "16px" }}>
        <div style={{ border: "1px solid #dee2e6", borderRadius: "4px", overflow: "hidden" }}>
          {custos.map((s, i) => (
            <div
              key={i}
              style={{
                padding: "8px 14px",
                fontSize: "0.85rem",
                borderBottom: i < custos.length - 1 ? "1px solid #f0f0f0" : "none",
                backgroundColor: "#fff",
              }}
            >
              {s}
            </div>
          ))}
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <CustosCelula>
      {visiveis.map((s, i) => (
        <span key={i} style={{ fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: "0 1 auto", minWidth: 0 }}>
          {s}
        </span>
      ))}
      {extras > 0 && (
        <OverlayTrigger
          placement="top"
          trigger={["hover", "focus"]}
          overlay={popover}
        >
          <TagCustoExtra>+{extras}</TagCustoExtra>
        </OverlayTrigger>
      )}
    </CustosCelula>
  );
}

export function ListarAguardando({
  dados,
  handleLimitChange,
  handlePageChange,
  totalRows,
  setRowsSelected,
  selectedRows,
  linhasPorPagina,
  onClickAprovar,
  onClickReprovar,
  onClickVerDetalhes,
  clearRows,
  pagamentoUrgenteAtivo,
  onTogglePagamentoUrgente,
  onBaixarRecibo,
  onCriarLote,
  onOrdenar,
  podeCriar,
  podeAprovar,
  podeReprovar,
}: IListarProps) {
  const [showModalColunas, setShowModalColunas] = useState(false);
  const [configColunas, setConfigColunas] = useState<
    { nome: string; exibir: boolean }[]
  >(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : defaultConfigColunas;
    } catch {
      return defaultConfigColunas;
    }
  });

  function handleSaveConfigColunas(
    novaConfig: { nome: string; exibir: boolean }[],
  ) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(novaConfig));
    setConfigColunas(novaConfig);
  }

  const omit = (nome: string) =>
    configColunas.some((c) => c.nome === nome && !c.exibir);

  const columns: IColum<IListarLoteGerenciarPagamento>[] = [
    {
      name: "ID Lote",
      selector: ({ id }: IListarLoteGerenciarPagamento) => id,
      width: "90px",
      omit: omit("idLote"),
      sortable: true,
      sortField: "id",
    },
    {
      name: "Usuário",
      selector: ({ nomeUsuario }: IListarLoteGerenciarPagamento) =>
        nomeUsuario ?? "-",
      width: "130px",
      omit: omit("usuario"),
      sortable: true,
      sortField: "nomeUsuario",
    },
    {
      name: "Recibo / NF",
      selector: ({
        numeroNfRecibo,
        pagamentoUrgente,
        semArquivoAnexado,
        id,
      }: IListarLoteGerenciarPagamento) => (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {!semArquivoAnexado ? (
            <span
              className="link-primary"
              style={{ cursor: "pointer" }}
              onClick={() => onBaixarRecibo(id, numeroNfRecibo ?? "")}
            >
              {numeroNfRecibo ?? "-"}
            </span>
          ) : (
            <span>{numeroNfRecibo ?? "-"}</span>
          )}
          {pagamentoUrgente && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <FaFlag style={{ marginRight: 4, color: "#ee5353" }} />
                  Pagamento urgente
                </Tooltip>
              }
            >
              <FlagUrgente>
                <FaFlag />
              </FlagUrgente>
            </OverlayTrigger>
          )}
          {semArquivoAnexado && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Sem arquivo Recibo / NF</Tooltip>}
            >
              <FlagSemArquivo>
                <FaImage />
              </FlagSemArquivo>
            </OverlayTrigger>
          )}
        </div>
      ),
      width: "180px",
      omit: omit("reciboNf"),
      sortable: true,
      sortField: "numeroNfRecibo",
    },
    {
      name: "Beneficiário",
      selector: ({ nomeBeneficiario }: IListarLoteGerenciarPagamento) =>
        nomeBeneficiario ?? "-",
      width: "140px",
      omit: omit("beneficiario"),
      sortable: true,
      sortField: "nomeBeneficiario",
    },
    {
      name: "Observação",
      selector: ({ observacao }: IListarLoteGerenciarPagamento) => (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{observacao ?? ""}</Tooltip>}
        >
          <span>
            {observacao?.trim()
              ? observacao.length > 25
                ? `${observacao.slice(0, 25)}...`
                : observacao
              : "-"}
          </span>
        </OverlayTrigger>
      ),
      width: "160px",
      omit: omit("observacao"),
      sortable: true,
      sortField: "observacao",
    },
    {
      name: "Custos",
      selector: ({ custosDescricao }: IListarLoteGerenciarPagamento) => (
        <CustosColunaCell custos={custosDescricao ?? []} />
      ),
      omit: omit("custosDescricao"),
      sortable: true,
      sortField: "custosDescricao",
    },
    {
      name: "Processos",
      selector: ({ qtdProcessos }: IListarLoteGerenciarPagamento) =>
        qtdProcessos ?? 0,
      width: "110px",
      omit: omit("processos"),
      sortable: true,
      sortField: "qtdProcessos",
    },
    {
      name: <span>Total a pagar</span>,
      selector: ({ totalAPagar }: IListarLoteGerenciarPagamento) =>
        totalAPagar != null ? formatarDinheiro(Number(totalAPagar)) : "-",
      width: "130px",
      omit: omit("totalAPagar"),
      sortable: true,
      sortField: "totalAPagar",
    },
    {
      name: (
        <OverlayTrigger placement="top" overlay={<Tooltip>Valor dos custos do processo</Tooltip>}>
          <span>Valor custos</span>
        </OverlayTrigger>
      ),
      selector: ({
        custos,
        alertaCustoTotalAPagar,
      }: IListarLoteGerenciarPagamento) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{custos != null ? formatarDinheiro(Number(custos)) : "-"}</span>
          {alertaCustoTotalAPagar && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <FaExclamationTriangle style={{ marginRight: 4, color: "#fe8f14" }} />
                  Custo do processo superior ao total a pagar.
                </Tooltip>
              }
            >
              <AlertaAReceber>
                <FaExclamationTriangle />
              </AlertaAReceber>
            </OverlayTrigger>
          )}
        </div>
      ),
      width: "120px",
      omit: omit("valorCustos"),
      sortable: true,
      sortField: "custos",
    },
    {
      name: (
        <OverlayTrigger placement="top" overlay={<Tooltip>Serviços a receber</Tooltip>}>
          <span>A receber</span>
        </OverlayTrigger>
      ),
      selector: ({
        aReceber,
        alertaAReceber,
      }: IListarLoteGerenciarPagamento) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>
            {formatarDinheiro(Number(aReceber ?? 0))}
          </span>
          {alertaAReceber && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <FaExclamationTriangle style={{ marginRight: 4, color: "#fe8f14" }} />
                  Valor do serviço a receber inferior ao custo do processo.
                </Tooltip>
              }
            >
              <AlertaAReceber>
                <FaExclamationTriangle />
              </AlertaAReceber>
            </OverlayTrigger>
          )}
        </div>
      ),
      width: "130px",
      omit: omit("aReceber"),
      sortable: true,
      sortField: "aReceber",
    },
    {
      name: "Ações",
      selector: ({ id }: IListarLoteGerenciarPagamento) => (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>Visualizar lote</Tooltip>}
        >
          <span style={{ cursor: "pointer" }} onClick={() => onClickVerDetalhes(id)}>
            <FaEye />
          </span>
        </OverlayTrigger>
      ),
      width: "70px",
    },
  ];

  const exportarExcel = () => {
    const dadosPlanilha = dados.map((item) => ({
      "ID Lote": item.id,
      Usuário: item.nomeUsuario ?? "-",
      "Recibo / NF": item.numeroNfRecibo ?? "-",
      Beneficiário: item.nomeBeneficiario ?? "-",
      Observação: item.observacao ?? "-",
      Custos: (item.custosDescricao ?? []).join(", "),
      Processos: item.qtdProcessos ?? 0,
      "Total a pagar": Number(item.totalAPagar ?? 0),
      "Valor custos": Number(item.custos ?? 0),
      "A receber": Number(item.aReceber ?? 0),
    }));

    const planilha = utils.json_to_sheet(dadosPlanilha);

    const colunasValor = [7, 8, 9];
    const range = utils.decode_range(planilha["!ref"] ?? "A1");
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      for (const col of colunasValor) {
        const cellRef = utils.encode_cell({ r: row, c: col });
        if (planilha[cellRef]) {
          planilha[cellRef].z = '"R$ "#,##0.00';
        }
      }
    }

    planilha["!cols"] = Array(10).fill({ wch: 20 });
    const workBook = utils.book_new();
    utils.book_append_sheet(workBook, planilha, "Aguardando Aprovação");

    const hoje = new Date();
    const dia = String(hoje.getDate()).padStart(2, "0");
    const mes = String(hoje.getMonth() + 1).padStart(2, "0");
    const ano = hoje.getFullYear();
    writeFileXLSX(workBook, `PAGAMENTOS_AGUARDANDO_${dia}-${mes}-${ano}.xlsx`);
  };

  return (
    <Container>
      <Header>
        <TextoHeader>Lotes aguardando aprovação</TextoHeader>
        <DivAcoes>
          <ButtonIcon
            outline
            variante="primary"
            onClick={exportarExcel}
            title="Exportar"
          >
            <FaDownload />
          </ButtonIcon>
          <Button
            outline
            variante="primary"
            onClick={() => setShowModalColunas(true)}
            title="Configurar colunas"
            style={{ width: "40px", padding: 0 }}
          >
            <FaColumns />
          </Button>
          {podeCriar && (
            <Button
              outline
              variante="primary"
              onClick={onCriarLote}
              style={{ width: "145px" }}
            >
              <FaPlus /> Criar novo lote
            </Button>
          )}
          {podeReprovar && (
            <Button
              outline
              variante="primary"
              disabled={!selectedRows.length}
              onClick={onClickReprovar}
              style={{ width: "135px" }}
            >
              <FaTimes /> Reprovar lote
            </Button>
          )}
          {podeAprovar && (
            <Button
              disabled={!selectedRows.length}
              onClick={onClickAprovar}
              style={{ width: "130px" }}
            >
              <FaCheck /> Aprovar lote
            </Button>
          )}
        </DivAcoes>
      </Header>
      <hr style={{ marginTop: "1.5rem" }} />
      <BarraFiltroRapido>
        <PagamentoUrgenteContainer>
          <SwitchCustom
            onChange={(e) => onTogglePagamentoUrgente(e.target.checked)}
            checked={pagamentoUrgenteAtivo}
            title="Filtrar apenas pagamentos urgentes"
          />
          Pagamento urgente
        </PagamentoUrgenteContainer>
      </BarraFiltroRapido>
      <Table
        keyField="id"
        totalRows={totalRows}
        pagination={true}
        columns={columns}
        data={dados}
        paginationRowsPerPageOptions={[30, 50, 100, 300]}
        loading={false}
        onChangeRowsPerPage={handleLimitChange}
        onChangePage={handlePageChange}
        noSubHeader
        possuiTooltip={false}
        selectableRows
        setRowsSelected={setRowsSelected}
        rowsPerPage={linhasPorPagina}
        headerFixo
        clearSelectedRows={clearRows}
        handleSortFunction={(coluna, direcao) =>
          onOrdenar((coluna.sortField as string) ?? "", direcao)
        }
      />
      <ModalConfigColunas
        show={showModalColunas}
        handleClose={() => setShowModalColunas(false)}
        configColunas={configColunas}
        onSave={handleSaveConfigColunas}
      />
    </Container>
  );
}
